import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { SfLog } from '@app/adapters/logger'
import { fetchColumnsRequest } from '@app/application/report/api/columns'

import { settleColumn } from './column-constraints'
import { fieldName, fieldValue, trackInlineColumns } from './track'

import type { HeapProperties } from '@app/adapters/heap'
import type {
  ApplyError,
  ColumnMoved,
  ColumnType,
  ColumnValues,
  EditorColumn,
  EditorStatus,
  TypeSchema,
} from './types'

// The column types the "Add a column" buttons can add, in the order they appear.
// Which of them a given report actually offers is the server's call (an
// analytical column needs an analytical or consolidation company) and arrives in
// `addable`, so this is the display order to filter by it rather than the list
// to show.
export const ADDABLE_TYPES = [
  'Period',
  'PeriodDifference',
  'PeriodAnalytical',
  'ColumnSpacer',
] as const

export type AddableType = typeof ADDABLE_TYPES[number]

let uidCounter = 0
const nextUid = (): string => {
  uidCounter += 1
  return `col-${uidCounter}`
}

// A copy that shares nothing with the original, so editing one list can't write
// through to the other.
const cloneColumns = (columns: EditorColumn[]): EditorColumn[] =>
  columns.map((column) => ({
    ...column,
    values: { ...column.values },
    displaced: { ...column.displaced },
  }))

const transition = (
  field: string,
  from: string | undefined,
  to: string | undefined
): string => `${field}:${fieldValue(from ?? '')}>${fieldValue(to ?? '')}`

const sameValues = (a: ColumnValues, b: ColumnValues): boolean => {
  const keys = Object.keys(a)

  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => a[key] === b[key])
  )
}

// Whether two lists would render the same report: exactly what an apply sends —
// each column's type and values, in order — and nothing besides. `uid` and `id`
// are the editor's own bookkeeping (a column removed and added back by hand
// renders identically, so it is not a change), and `displaced` is what the value
// rules remember, never sent.
const sameColumns = (a: EditorColumn[], b: EditorColumn[]): boolean =>
  a.length === b.length &&
  a.every(
    (column, index) =>
      column.type === b[index].type &&
      sameValues(column.values, b[index].values)
  )

// The working state of the inline column editor drawer. Lives in a store rather
// than in the Stimulus controller because both the controller (which opens the
// drawer and knows which report is on screen) and the Vue components need it.
export const useColumnEditor = defineStore('reportColumnEditor', () => {
  const isOpen = ref(false)
  const status = ref<EditorStatus>('idle')
  const columns = ref<EditorColumn[]>([])

  // The report's own columns, as they were loaded. Kept so the drawer can say
  // whether anything has been changed, and put them back when it is asked to.
  //
  // It stays the report's saved config for as long as the drawer lives: `load`
  // is a no-op once the columns are in hand, and applying persists nothing — so
  // after an apply this is still what the next page load would bring back.
  const loadedColumns = ref<EditorColumn[]>([])

  // The columns the table on screen was rendered for, once an apply has put one
  // there. Null until then, when the table is still the report's own — so what
  // the report currently shows is this or, failing that, `loadedColumns`.
  const appliedColumns = ref<EditorColumn[] | null>(null)

  // The list the apply in flight asked for, waiting to become the above.
  let pendingColumns: EditorColumn[] = []

  const schema = ref<Partial<Record<ColumnType, TypeSchema>>>({})
  const defaults = ref<Partial<Record<ColumnType, ColumnValues>>>({})
  const addable = ref<ColumnType[]>([])

  const reportId = ref<number | null>(null)
  const reportPeriodSetId = ref<number | null>(null)

  // The year the report's forecast column is forecasting, when it has one. Not
  // one of the report's columns — it is unsaved work sitting on the page, owned
  // by the forecast controller — so it is listed as a row that can't be edited,
  // moved or removed here, and takes no part in what is applied. The controller
  // keeps this in step with what the forecast says (see events.ts).
  const forecastYear = ref<number | null>(null)

  function setForecastYear(year: number | null): void {
    forecastYear.value = year
  }

  // Applying is the drawer's ask but the controller's work: it means replacing
  // the report table, which is page DOM the drawer doesn't own. A counter rather
  // than a flag, so applying the same columns twice is two requests instead of a
  // flag flicked back to where it started.
  const applyRequests = ref(0)
  const applying = ref(false)

  // Why the last apply failed, not just that it did: a render the server is too
  // large to attempt is the one failure the user can do something about, and it
  // reads as a bug unless the drawer says which it was.
  const applyError = ref<ApplyError | null>(null)
  const applyFailed = computed(() => applyError.value !== null)

  // Previewing is the controller's work too, and it is the only thing that ever
  // reports on it: a preview that never lands leaves the drawer's controls
  // looking like they do nothing, so the drawer has to be able to say so.
  const previewFailed = ref(false)

  // Per drawer session, reset by open(). Only ever reported when the session
  // ends without an apply.
  let userEdits = 0
  const expandedColumns = new Set<string>()

  // Which time this is that the drawer has been opened on this page. Kept apart
  // from the controller's `openGeneration`, which counts the same thing but
  // guards request staleness: a change made for a metric must not reach that.
  let openIndex = 0

  // Stamped on every event the drawer raises: which trip through it this is, and
  // whether the report carried a forecast column at the time. The forecast is
  // unsaved page state owned by another controller, so nothing server-rendered
  // can answer for it.
  function drawerContext(): HeapProperties {
    return {
      open_index: openIndex,
      has_forecast: forecastYear.value !== null,
    }
  }

  function drawerActivity(): HeapProperties {
    return {
      ...drawerContext(),
      drawer_edited: userEdits > 0,
      drawer_edit_count: userEdits,
      drawer_expanded_count: expandedColumns.size,
    }
  }

  function noteExpanded(uid: string): void {
    expandedColumns.add(uid)
  }

  // Raised here because this is the only place a value the user picked is still
  // distinguishable from one the rules wrote in answer.
  function trackEdit(properties: HeapProperties): void {
    userEdits += 1
    trackInlineColumns('Column Edited', {
      ...drawerContext(),
      column_source: 'user',
      ...properties,
    })
  }

  // Values the rules wrote, not the user. Deliberately outside `userEdits`, or
  // every engagement number counts the form arguing with itself.
  function trackRuleEdit(properties: HeapProperties): void {
    trackInlineColumns('Column Edited', {
      ...drawerContext(),
      column_source: 'rule',
      column_action: 'update',
      ...properties,
    })
  }

  function previewSucceeded(): void {
    previewFailed.value = false
  }

  function previewErrored(): void {
    previewFailed.value = true
  }

  function requestApply(): void {
    applyRequests.value += 1
  }

  // The work is the controller's, but the state is the store's: these keep the
  // transitions where the flags live, so what moves them is readable here rather
  // than only in the Stimulus controller driving the request.
  function applyStarted(): void {
    applying.value = true
    applyError.value = null

    // Taken now: the drawer stays editable while the render is on its way.
    pendingColumns = cloneColumns(columns.value)
  }

  function applyErrored(reason: ApplyError = 'generic'): void {
    applyError.value = reason
  }

  // What the table was rendered for, which is the snapshot `applyStarted` took.
  function applyRendered(): void {
    appliedColumns.value = pendingColumns
  }

  // However it ended — rendered, failed, or abandoned when the drawer closed
  // under it — the request is no longer in flight.
  function applyFinished(): void {
    applying.value = false
  }

  // A failed apply describes the columns that were applied. Change them and it
  // describes nothing on screen any more — the preview has already moved on — so
  // it goes as soon as the list does.
  function clearApplyError(): void {
    applyError.value = null
  }

  // Which report — and which of its period sets — the drawer edits. Comes from
  // the subnav button's action params.
  //
  // Everything below belongs to these two ids, so pointing the drawer at a
  // different report (or period set) drops what was loaded for the old one.
  // Without that, load()'s already-loaded guard would serve the previous
  // report's columns.
  //
  // No page can currently make that happen: the ids are baked into the subnav
  // button at render time, and a Turbolinks visit tears the whole Pinia
  // instance down (see adapters/singleton/page). The reset is here because the
  // store's ids are settable and the guard downstream trusts them, not to
  // defend against a caller we have.
  function configure(ids: {
    reportId: number
    reportPeriodSetId: number | null
  }): void {
    if (
      reportId.value === ids.reportId &&
      reportPeriodSetId.value === ids.reportPeriodSetId
    ) {
      return
    }

    reportId.value = ids.reportId
    reportPeriodSetId.value = ids.reportPeriodSetId

    columns.value = []
    loadedColumns.value = []
    appliedColumns.value = null
    pendingColumns = []
    forecastYear.value = null
    schema.value = {}
    defaults.value = {}
    addable.value = []
    status.value = 'idle'
    applying.value = false
    applyError.value = null
    previewFailed.value = false
  }

  // Loads the columns of the configured report. A no-op while a request is in
  // flight or once the columns are in hand: re-opening the drawer keeps
  // in-progress edits, and a second click can't race two responses into the
  // same state. Calling it from the error state retries.
  async function load(): Promise<void> {
    if (!reportId.value) return
    if (status.value === 'loading' || status.value === 'loaded') return

    status.value = 'loading'

    try {
      const response = await fetchColumnsRequest({
        reportId: reportId.value,
        reportPeriodSetId: reportPeriodSetId.value,
      })

      // Each column gets a stable client-side uid so edits don't lose track of
      // rows (the server id is null for columns added here).
      const loaded = response.columns.map((column) => ({
        uid: nextUid(),
        id: column.id,
        type: column.type,
        values: { ...column.values },
        displaced: {},
      }))

      columns.value = loaded
      loadedColumns.value = cloneColumns(loaded)
      schema.value = response.schema
      defaults.value = response.defaults
      // Falls back the way `schema` and `defaults` do through their own reads: a
      // response from a server that predates the field (the window a rolling
      // deploy leaves open) then costs the add buttons rather than throwing out
      // of the drawer's whole `loaded` branch.
      addable.value = response.addable ?? []
      status.value = 'loaded'
    } catch (error) {
      SfLog.error("Failed to load the report's columns", {
        error,
        extra: { reportId: reportId.value },
      })
      status.value = 'error'
    }
  }

  function open(): void {
    isOpen.value = true
    openIndex += 1
    userEdits = 0
    expandedColumns.clear()
  }

  // Both errors describe what the drawer was doing to the report on screen, and
  // closing it undoes that — the preview comes off and the report is its own
  // again. Left standing, they would greet the next open with a complaint about
  // a request from before it (`configure` only resets them when the drawer is
  // pointed at a different report, which no page currently does).
  // `applying` too, or the next open gets a loading button its guard refuses.
  function close(): void {
    isOpen.value = false
    applying.value = false
    applyError.value = null
    previewFailed.value = false
  }

  // Only the types the "+" buttons offer: `defaults` carries starting values for
  // exactly those, so adding any other type would push a column with no values
  // and expand into a form of empty selects.
  function addColumn(type: AddableType): void {
    clearApplyError()
    columns.value.push({
      uid: nextUid(),
      id: null,
      type,
      values: { ...(defaults.value[type] ?? {}) },
      displaced: {},
    })

    trackEdit({ column_action: 'add', column_type: type })
  }

  // Whether the list still describes the report's own columns. Drives the clear
  // control, and is false while the columns are on their way — both lists are
  // empty then, so there is nothing to undo yet.
  const hasChanges = computed(
    () => !sameColumns(columns.value, loadedColumns.value)
  )

  // Whether applying would render anything other than the table already on
  // screen. Drives Apply, and it is deliberately not `hasChanges`: after an
  // apply, clearing takes the list back to the report's own columns, which is a
  // change worth applying precisely because the table is not showing them.
  const hasUnappliedChanges = computed(
    () =>
      !sameColumns(columns.value, appliedColumns.value ?? loadedColumns.value)
  )

  // Back to the report's own columns. Only the drawer's list, so the preview
  // follows and nothing else does: a table that was applied stays applied until
  // Apply is clicked again — this time with the columns the report started with.
  function clearChanges(): void {
    clearApplyError()
    columns.value = cloneColumns(loadedColumns.value)

    trackEdit({ column_action: 'reset' })
  }

  function removeColumn(uid: string): void {
    clearApplyError()
    const index = columns.value.findIndex((column) => column.uid === uid)
    if (index === -1) return

    const [removed] = columns.value.splice(index, 1)

    trackEdit({ column_action: 'remove', column_type: removed.type })
  }

  // The list order *is* the column order, so a drag has nothing to keep in step
  // beyond the array vuedraggable hands back (the same rows, resequenced).
  function reorderColumns(reordered: EditorColumn[]): void {
    clearApplyError()

    columns.value = reordered
  }

  // Positions come off the event: the list is already reordered by the time this
  // arrives, and the arrays cannot say which row an adjacent swap picked up.
  function noteReorder(moved: ColumnMoved): void {
    const from = moved.oldIndex + 1
    const to = moved.newIndex + 1

    trackEdit({
      column_action: 'reorder',
      column_type: moved.element.type,
      column_field: 'position',
      column_from: from,
      column_to: to,
      column_transition: transition('position', String(from), String(to)),
    })
  }

  function updateColumnValue(uid: string, key: string, value: string): void {
    const column = columns.value.find((candidate) => candidate.uid === uid)
    if (!column) return

    clearApplyError()

    const before = { ...column.values }

    // The user picking a value for a field settles that field: whatever a rule
    // had displaced there is no longer theirs to get back.
    delete column.displaced[key]
    column.values[key] = value

    // Some values rule others out, and the rest of the column has to follow:
    // choosing actuals plus budget makes it a whole-year column, so a period or
    // year saying otherwise moves to one that agrees — and moves back when the
    // rule lifts. Applied here rather than in the form so the column is never
    // briefly in a state it can't be saved in — everything else reading the
    // store sees a column that already agrees with itself.
    Object.assign(column, settleColumn(column, schema.value[column.type], key))

    const field = fieldName(column.type, key)

    trackEdit({
      column_action: 'update',
      column_type: column.type,
      column_field: field,
      column_from: fieldValue(before[key] ?? null),
      column_to: fieldValue(value),
      column_transition: transition(field, before[key], value),
    })

    Object.keys(column.values)
      .filter(
        (ruled) => ruled !== key && column.values[ruled] !== before[ruled]
      )
      .forEach((ruled) => {
        const ruledField = fieldName(column.type, ruled)

        trackRuleEdit({
          column_type: column.type,
          column_field: ruledField,
          column_from: fieldValue(before[ruled] ?? null),
          column_to: fieldValue(column.values[ruled]),
          column_transition: transition(
            ruledField,
            before[ruled],
            column.values[ruled]
          ),
        })
      })
  }

  return {
    isOpen,
    status,
    reportId,
    columns,
    loadedColumns,
    appliedColumns,
    forecastYear,
    hasChanges,
    hasUnappliedChanges,
    schema,
    defaults,
    addable,
    applyRequests,
    applying,
    applyFailed,
    applyError,
    previewFailed,
    requestApply,
    applyStarted,
    applyErrored,
    applyRendered,
    applyFinished,
    previewSucceeded,
    previewErrored,
    drawerActivity,
    drawerContext,
    noteExpanded,
    configure,
    setForecastYear,
    load,
    open,
    close,
    addColumn,
    clearChanges,
    removeColumn,
    reorderColumns,
    noteReorder,
    updateColumnValue,
  }
})
